import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { ADMIN_BTN_GHOST, ADMIN_BTN_PRIMARY, ADMIN_CARD } from './adminTheme';

function formatUptimeShort(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '—';
  const sec = Math.floor(s);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
}

/**
 * Per-service and stack-wide container restart + status check.
 * Shows live CPU/RAM (docker stats) and uptime since last container start.
 */
export default function AdminContainerControls({ onToast, onError }) {
  const base = getApiBaseUrl();
  const [containers, setContainers] = useState([]);
  const [restartable, setRestartable] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${base}/admin/containers/status`, adminFetchInit());
      if (!res.ok) throw new Error(`containers/status HTTP ${res.status}`);
      const data = await res.json();
      setContainers(Array.isArray(data?.containers) ? data.containers : []);
      setRestartable(Array.isArray(data?.restartable) ? data.restartable : []);
      if (data?.ok === false && data?.error) {
        setError(String(data.error));
      }
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [base, onError]);

  useEffect(() => {
    loadStatus();
    const id = window.setInterval(loadStatus, 15000);
    return () => window.clearInterval(id);
  }, [loadStatus]);

  const restart = useCallback(
    async ({ service, all } = {}) => {
      const key = all ? 'all' : String(service || '');
      if (!key || busy) return;
      setBusy(key);
      setError('');
      try {
        const res = await fetch(
          `${base}/admin/containers/restart`,
          adminFetchInit({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(all ? { all: true } : { service }),
          }),
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.ok === false) {
          throw new Error(data?.error || `restart HTTP ${res.status}`);
        }
        onToast?.(
          all
            ? `Restarted allowlisted stack (${(data.services || []).join(', ') || 'all'})`
            : `Restarted ${service}`,
        );
        await loadStatus();
      } catch (e) {
        const msg = String(e?.message || e);
        setError(msg);
        onError?.(msg);
      } finally {
        setBusy('');
      }
    },
    [base, busy, loadStatus, onError, onToast],
  );

  const byService = Object.fromEntries(
    containers.map((c) => [String(c.service || c.name || ''), c]),
  );

  return (
    <section className={ADMIN_CARD} data-testid="admin-container-controls">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
            Container restart & status
          </h2>
          <p className="mt-1 text-[11px] text-slate-400">
            Live CPU/RAM from <span className="font-mono">docker stats</span> and uptime since last container
            start. Restarting <span className="font-mono">backend</span> may briefly interrupt this admin
            session. Needs working docker.sock (<span className="font-mono">DOCKER_GID</span>).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={loadStatus} disabled={loading || Boolean(busy)} className={ADMIN_BTN_GHOST}>
            {loading ? 'Checking…' : 'Status check'}
          </button>
          <button
            type="button"
            onClick={() => restart({ all: true })}
            disabled={Boolean(busy)}
            className={ADMIN_BTN_PRIMARY}
            data-testid="admin-restart-all"
          >
            {busy === 'all' ? 'Restarting…' : 'Restart all'}
          </button>
        </div>
      </div>
      {error ? <p className="mb-2 text-[11px] text-rose-300">{error}</p> : null}
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full min-w-[820px] border-collapse text-left text-xs">
          <thead className="border-b border-white/10 bg-slate-950 text-[10px] font-black uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Service</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">CPU</th>
              <th className="px-3 py-2">RAM</th>
              <th className="px-3 py-2">Up since start</th>
              <th className="px-3 py-2">Health</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {(restartable.length ? restartable : Object.keys(byService)).map((svc) => {
              const row = byService[svc] || {};
              const cpu =
                typeof row.cpu_percent === 'number' ? `${row.cpu_percent}%` : '—';
              const ram =
                typeof row.mem_used_mb === 'number'
                  ? `${row.mem_used_mb} / ${row.mem_limit_mb ?? '—'} MB` +
                    (typeof row.mem_percent === 'number' ? ` (${row.mem_percent}%)` : '')
                  : row.mem_usage_raw || '—';
              const up = formatUptimeShort(row.uptime_s);
              return (
                <tr key={svc} className="border-b border-white/5 last:border-0">
                  <td className="px-3 py-2 font-bold text-slate-100">{svc}</td>
                  <td className="px-3 py-2 font-mono text-slate-300">{row.state || '—'}</td>
                  <td className="px-3 py-2 font-mono text-cyan-200">{cpu}</td>
                  <td className="px-3 py-2 font-mono text-amber-100" title={row.mem_usage_raw || ''}>
                    {ram}
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-slate-300"
                    title={row.started_at || ''}
                  >
                    {up}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-400">{row.health || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => restart({ service: svc })}
                      className={ADMIN_BTN_GHOST}
                      data-testid={`admin-restart-${svc}`}
                    >
                      {busy === svc ? '…' : 'Restart'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
